/**
 * Maps backend notification `event` keys to a presentation icon and a toast
 * variant. Only customer-facing events are listed; anything unknown falls back
 * to a neutral bell so new backend events still render gracefully.
 *
 * `variant` reuses the design-system toast variants (`@finmin/ui`) so the icon
 * tint and the toast accent stay in sync.
 */
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Coins,
  CreditCard,
  FileCheck,
  FileText,
  Gift,
  MessageSquare,
  Sparkles,
  Upload,
  UserCheck,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { ToastVariant } from '@finmin/ui';

export interface EventMeta {
  Icon: LucideIcon;
  variant: ToastVariant;
}

const EVENT_META: Record<string, EventMeta> = {
  // ── Account / rewards ──────────────────────────────────────────────────────
  customer_signup:            { Icon: Sparkles,      variant: 'success' },
  signup_rewards_credited:    { Icon: Coins,         variant: 'success' },
  referral_successful:        { Icon: Gift,          variant: 'success' },

  // ── Service request lifecycle ──────────────────────────────────────────────
  service_registration:       { Icon: FileText,      variant: 'default' },
  admin_submits_summary:      { Icon: AlertCircle,   variant: 'warning' }, // action required
  customer_uploads_docs:      { Icon: Upload,        variant: 'default' },
  provider_accepts_customer:  { Icon: UserCheck,     variant: 'success' },

  // ── Chat ───────────────────────────────────────────────────────────────────
  provider_sends_message:     { Icon: MessageSquare, variant: 'default' },
  admin_sends_message_customer:{ Icon: MessageSquare, variant: 'default' },

  // ── Draft & payment ────────────────────────────────────────────────────────
  admin_approves_draft:       { Icon: AlertCircle,   variant: 'warning' }, // action required
  payment_success_customer:   { Icon: CreditCard,    variant: 'success' },
  wallet_used:                { Icon: Wallet,        variant: 'default' },
  wallet_credited:            { Icon: Coins,         variant: 'success' },

  // ── Final submission & completion ──────────────────────────────────────────
  admin_approves_final:       { Icon: FileCheck,     variant: 'success' },
  service_completed_customer: { Icon: CheckCircle2,  variant: 'success' },
};

const DEFAULT_META: EventMeta = { Icon: Bell, variant: 'default' };

export function getEventMeta(event: string): EventMeta {
  return EVENT_META[event] ?? DEFAULT_META;
}
